import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';



import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';



import { AuthContext } from '../../context/AuthContext';
import { dataCache } from '../../utils/dataCache';
import { slugify } from '../../utils/slugify';
import { supabase } from '../../utils/supabase';


const CourseImage = ({
  courseName,
  clubName,
  courseImages,
  visibleData,
  openName,
}) => {
  const defaultImageUrl =
    "https://api.golflists.com/storage/v1/object/public/GolfLists%20Public/no_course_image.jpg?t=2024-02-09T10%3A32%3A30.832Z";

  const courseImageFromProps = courseImages.find(
    (img) => img.course_name === courseName && img.image_url,
  );
  const visibleDataProp = visibleData.find(
    (course) => course.course_name === courseName,
  );
  const { user, editableCourses, editPermissions, username } =
    useContext(AuthContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [courseImage, setCourseImage] = useState(courseImageFromProps); //currently for club image we still access the course image
  const [selectedFileName, setSelectedFileName] = useState("");
  const [courseData, setCourseData] = useState(visibleDataProp); //we use different logic for courseData vs clubData
  const [clubData, setClubData] = useState(visibleDataProp);
  const [upvotedList, setUpvotedList] = useState([]);
  const [upvoteCounts, setUpvoteCounts] = useState([]);
  const [isUpvoted, setIsUpvoted] = useState(false);
  const fileInputId = `fileInput-${clubName ? slugify(clubName) : slugify(courseName)}`;

  //console.log("visibleData CourseImage", visibleData); //the repetition of this in the log suggest it is a bit inefficient MT NOTE
  //console.log(" CourseImage", courseImages); //the repetition of this in the log suggest it is a bit inefficient MT NOTE

  const fetchUpvoteCounts = async () => {
    try {
      const data = await dataCache.getUpvoteCounts();
      setUpvoteCounts(data ?? []);
    } catch (error) {
      console.error("Error fetching upvote counts:", error);
    }
  };

  useEffect(() => {
    fetchUpvoteCounts();
  }, []);

  const upvoteCount = upvoteCounts.find(
    (item) => item.course_name === courseName,
  )?.upvote_count;

  //console.log("Upvote Counts", upvoteCounts);
  //console.log("Upvote Count", upvoteCount);

  useEffect(() => {
    // Fetch current upvote status and count
    const fetchData = async () => {
      if (user) {
        const { data: upvotedData } = await supabase
          .from("course_votes")
          .select("*")
          .eq("user_that_voted", user.id);
        const votes = upvotedData ?? [];
        setUpvotedList(votes);
        const isCourseUpvoted = votes.some(
          (item) => item.course_name === courseName && item.course_upvoted,
        );
        setIsUpvoted(isCourseUpvoted);
      } else {
        setUpvotedList([]);
        setIsUpvoted(false);
      }
    };
    fetchData();
  }, [user, courseName]);

  const canUploadImage = () => {
    if (!user) return false;
    if (
      editPermissions === "Advanced" ||
      editPermissions === "Ambassador" ||
      editPermissions === "Editor"
    )
      return true;
    if (editPermissions === "Basic" && editableCourses.includes(courseName))
      return true;
    return false;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file); // <-- Use the setSelectedFile function here
      setSelectedFileName(file.name);
    }
  };

  useEffect(() => {
    // Attempt to find a course image first
    let image = courseImages.find(
      (img) => img.course_name === courseName && img.image_url,
    );

    // If a course image isn't found, try to find a club image
    if (!image) {
      const club = visibleData.find((club) => club.club_name === clubName);
      if (club && club.clubCourses) {
        image = club.clubCourses.reduce((acc, course) => {
          if (acc) return acc; // If an image is already found, skip further iterations
          return courseImages.find(
            (img) => img.course_uid === course.course_uid && img.image_url,
          );
        }, null);
      }
    }

    // Set the found image, or undefined if none was found
    setCourseImage(image);
  }, [courseImages, courseName, visibleData, clubName]);

  useEffect(() => {
    setCourseData(
      visibleData.find((course) => course.course_name === courseName),
    );
  }, [visibleData, courseName]);

  useEffect(() => {
    setClubData(visibleData.find((club) => club.club_name === clubName));
  }, [visibleData, clubName]);

  const onUpload = async () => {
    if (!selectedFile) {
      alert(
        "The photo upload did not work, please refresh the page and try again.",
      );
      return;
    }

    const folderName = courseName.replace(/[^a-zA-Z0-9_-]/g, "_");

    if (!user) {
      alert(
        "The photo upload did not work, please refresh the page and try again.",
      );
      console.error("User is not authenticated.");
      return;
    }

    // Upload the file using the standard upload method
    const { error } = await supabase.storage
      .from("course_images")
      .upload(`${folderName}/${selectedFile.name}`, selectedFile);

    if (error) {
      alert(
        "The photo upload did not work, please refresh the page and try again.",
      );
      console.error("Error uploading file:", error);
      return;
    }

    const imageUrl = `https://stjbqlfgvlefqghlyumg.supabase.co/storage/v1/object/public/course_images/${folderName}/${selectedFile.name}`;
    const externalLink = `https://golflists.com/profile/${username}`;

    // Now visibleData contains all the requested fields
    let courseUid;

    if (clubData && clubData.clubCourses && clubData.clubCourses.length > 0) {
      // If clubData is present and has clubCourses, use the course_uid of the first course
      courseUid = clubData.clubCourses[0].course_uid;
    } else if (courseData) {
      // Otherwise, fall back to using course_uid from courseData
      courseUid = courseData.course_uid;
    }
    // Access other fields like visibleData?.course_county, visibleData?.england_rank_gl, etc.

    // Check if a row with the given course_name already exists
    const { data: existingRow, error: queryError } = await supabase
      .from("course_images")
      .select("*")
      .eq("course_name", courseName)
      .single();

    if (queryError) {
      console.error("Error querying course_images:", queryError);
      // Handle the error as needed
    }

    if (existingRow) {
      // Update the existing row
      await supabase
        .from("course_images")
        .update({
          image_url: imageUrl,
          username: username,
          credit: username,
          external_link: externalLink,
        })
        .eq("course_name", courseName);
    } else {
      // Insert a new row
      await supabase.from("course_images").insert({
        course_name: courseName,
        image_url: imageUrl,
        user_uid: user.id, // Assuming user object has an id property for user_uid
        username: username,
        credit: username,
        external_link: externalLink,
        course_uid: courseUid,
        caption: courseName,
      });
    }

    if (!error) {
      alert("Image uploaded successfully!");

      // Update the local state to immediately display the uploaded image
      setCourseImage({
        course_name: courseName,
        image_url: imageUrl,
        external_link: externalLink,
        credit: username,
      });
    }
  };

  const handleUpvoteClick = useCallback(async () => {
    if (!user) {
      alert(
        "Sign in to golflists.com to upvote courses you love! We use your feedback to adjust our rankings.",
      );
      return;
    }
    try {
      const { data: existingVotes, error } = await supabase
        .from("course_votes")
        .select("*")
        .eq("user_that_voted", user.id)
        .eq("course_name", courseName);
      if (error) {
        console.error("Error fetching existing votes:", error);
        return;
      }
      const existingVote = existingVotes.length > 0 ? existingVotes[0] : null;
      if (existingVote && existingVote.id) {
        await supabase
          .from("course_votes")
          .update({ course_upvoted: !existingVote.course_upvoted })
          .eq("id", existingVote.id);
        const updatedUpvotedList = upvotedList.map((item) =>
          item.course_name === courseName
            ? { ...item, course_upvoted: !existingVote.course_upvoted }
            : item,
        );
        setUpvotedList(updatedUpvotedList);
        setIsUpvoted(!existingVote.course_upvoted);

        // Update the upvote count in the state
        const newUpvoteCounts = upvoteCounts.map((item) =>
          item.course_name === courseName
            ? {
                ...item,
                upvote_count:
                  item.upvote_count + (existingVote.course_upvoted ? -1 : 1),
              }
            : item,
        );
        setUpvoteCounts(newUpvoteCounts);
      } else {
        const { error, status } = await supabase
          .from("course_votes")
          .insert({
            course_name: courseName,
            course_upvoted: true,
            course_downvoted: false,
            user_that_voted: user.id,
          })
          .single();
        if (error && !error.message.includes("violates unique constraint")) {
          console.error("Error details:", error);
          return;
        }
        if (status === 201) {
          const updatedUpvotedList = [
            ...upvotedList,
            {
              course_name: courseName,
              course_upvoted: true,
              course_downvoted: false,
              user_that_voted: user.id,
            },
          ];
          setUpvotedList(updatedUpvotedList);
          setIsUpvoted(true);

          // Add the new upvote to the count
          const newUpvoteCounts = upvoteCounts.map((item) =>
            item.course_name === courseName
              ? { ...item, upvote_count: item.upvote_count + 1 }
              : item,
          );
          setUpvoteCounts(newUpvoteCounts);
        }
      }
      // Fetch the latest upvote counts
      await fetchUpvoteCounts();
    } catch (error) {
      console.error(error);
    }
  }, [courseName, upvotedList, user, upvoteCounts]);

  const overlayPill =
    'text-white text-xxs px-2 py-1 rounded-full font-medium backdrop-blur-sm bg-black/40';

  const renderOverlay = () => (
    <>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/50 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 w-full flex flex-col items-center justify-start pt-4 gap-y-2 px-3">
        {clubName ? (
          <>
            <Link
              className="w-full"
              to={`/lists/golf-clubs/${slugify(clubName)}`}
            >
              <h3 className="mx-auto text-white text-center text-base font-bold truncate xs:text-sm w-11/12 drop-shadow-lg">
                {clubName}
              </h3>
            </Link>
            <div className="px-2 w-full flex flex-row flex-wrap justify-center gap-1.5 items-center">
              {clubData?.club_country && (
                <span className={overlayPill}>{clubData.club_country}</span>
              )}
              {clubData?.club_county && (
                <Link
                  to={`/lists/golf-courses/county-ranking/${slugify(
                    clubData?.club_county,
                  )}`}
                >
                  <span className={`${overlayPill} hover:bg-black/60 transition-colors`}>
                    {clubData?.club_county}
                  </span>
                </Link>
              )}
            </div>
          </>
        ) : openName ? (
          <>
            <Link
              className="w-full"
              to={`/lists/opens/${slugify(openName)}`}
            >
              <h3 className="mx-auto text-white text-center text-base font-bold truncate xs:text-sm w-11/12 drop-shadow-lg">
                {openName}
              </h3>
            </Link>
            <div className="px-2 w-full flex flex-row flex-wrap justify-center gap-1.5 items-center">
              {courseData?.club_name && (
                <span className={overlayPill}>{courseData.club_name}</span>
              )}
              {courseData?.course_county && (
                <span className={overlayPill}>{courseData.course_county}</span>
              )}
              {courseData?.start_date && (
                <span className={overlayPill}>{courseData.start_date}</span>
              )}
              {courseData?.open_fee && (
                <span className={overlayPill}>£{courseData.open_fee}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <Link className='w-full'
              to={`/lists/golf-courses/${slugify(courseData?.course_name)}`}
            >
              <h3 className="mx-auto text-white text-center text-base font-bold truncate xs:text-sm w-11/12 drop-shadow-lg">
                {courseData?.course_name}
              </h3>
            </Link>
            <div className="px-2 w-full flex flex-row flex-wrap justify-center gap-1.5 items-center">
              {courseData?.course_country && (
                <span className={overlayPill}>
                  {courseData.course_country} Rank #{courseData.country_rank}
                </span>
              )}
              {courseData?.course_county && (
                <Link
                  to={`/lists/golf-courses/county-ranking/${slugify(
                    courseData?.course_county,
                  )}`}
                >
                  <span className={`${overlayPill} hover:bg-black/60 transition-colors`}>
                    {courseData.course_county} Rank #{courseData.county_rank}
                  </span>
                </Link>
              )}
              {courseData?.course_style && (
                <span className={overlayPill}>{courseData.course_style}</span>
              )}
            </div>

            <button
              onClick={() => handleUpvoteClick(courseData?.course_name)}
              className="min-w-[40px] absolute top-0 left-0 m-2 rounded-lg text-white shadow-lg backdrop-blur-sm bg-black/50 hover:bg-black/70 transition-colors border border-white/20"
            >
              <FontAwesomeIcon
                icon={isUpvoted ? solid('circle-down') : solid('circle-up')}
                className={`text-xs ${isUpvoted ? 'text-red-400' : 'text-emerald-400'}`}
              />
              <span className="ml-1 text-sm font-medium">{upvoteCount || 0}</span>
            </button>
          </>
        )}
      </div>
      <a
        href={`/lists/golf-courses/${slugify(courseData?.course_name)}` || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-2 left-2 min-w-[60px] rounded-full text-xs flex items-center gap-1 px-3 py-1.5 shadow-md backdrop-blur-sm bg-white/95 text-slate-800 hover:bg-white transition-all duration-200 font-medium"
      >
        <FontAwesomeIcon icon={solid('money-bill-wave')} className="text-xxs" />
        {clubData && clubData.clubCourses && clubData.clubCourses.length > 0
          ? clubData.clubCourses[0].course_country === 'Ireland'
            ? '€'
            : '£'
          : courseData?.course_country === 'Ireland'
            ? '€'
            : '£'}
        {clubData && clubData.clubCourses && clubData.clubCourses.length > 0
          ? clubData.clubCourses[0].weekend_green_fee || '?'
          : courseData?.weekend_green_fee || '?'}
      </a>

      <a
        href={courseImage?.external_link || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-2 right-2 rounded-full text-xs flex items-center gap-1 px-3 py-1.5 shadow-md backdrop-blur-sm bg-white/95 text-slate-800 hover:bg-white transition-all duration-200 font-medium"
      >
        <FontAwesomeIcon icon={solid('camera')} />
        {courseImage?.credit || username}
      </a>
    </>
  );

  return (
    <div className="relative w-full h-auto">
      {courseImage ? (
        <>
        <div className="w-full relative">
          <img
            loading="lazy"
            src={courseImage.image_url}
            alt={courseImage.caption}
            className="object-cover h-[180px] max-h-[180px] sm:h-[220px] sm:max-h-[220px] md:h-[260px] md:max-h-[260px] w-full rounded-t-xl md:rounded-l-xl md:rounded-r-none overflow-hidden"
          />
            {renderOverlay()}
          </div>
        </>
      ) : canUploadImage() ? (
        // Scenario 1: User logged in with permission, no course image provided
        // The div below uses the default image as a background for the upload widget
        <div
          className="mx-auto flex flex-col items-center justify-center h-[180px] max-h-[180px] sm:h-[220px] sm:max-h-[220px] w-full border-dashed border-2 p-1"
          style={{
            backgroundImage: `url(${defaultImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <p className="text-black text-center">
            For: {clubName ? clubName : courseName}
          </p>

          <label
            htmlFor={fileInputId}
            className="cursor-pointer bg-gray-600 text-white px-4 p-1 rounded-sm hover:bg-gray-700"
          >
            Select Image
          </label>
          <input
            id={fileInputId}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
          <span className="text-sm text-black">
            {selectedFileName || "No Image Selected"}
          </span>
          <button
            onClick={onUpload}
            className="mt-2 bg-green-600 p-1 text-white rounded-sm"
          >
            Upload Image
          </button>
          <p className="text-xs text-gray-500 px-10 text-center italic">
            <span className="font-semibold">Note:</span> if the upload button
            does not work, refresh the page and try again.
          </p>
        </div>
      ) : (
        <>
          {/* Scenario 4: User not logged in, no course image provided */}
          <img
            loading="lazy"
            src={defaultImageUrl}
            alt="Placeholder Course"
            className="object-cover h-[180px] max-h-[180px] sm:h-[220px] sm:max-h-[220px] w-full rounded-t-lg"
          />
          {renderOverlay()}
        </>
      )}
    </div>
  );
};

export default CourseImage;